<template>
  <div class="kartu-export-engine" aria-hidden="true">
    <canvas ref="frontCanvas" width="650" height="1017"></canvas>
    <canvas ref="backCanvas" width="650" height="1017"></canvas>
    <div class="qr-render-source">
      <vue-qrcode
        v-if="cardQrValue"
        ref="qrSource"
        :value="cardQrValue"
        :options="{ width: 276, height: 276, margin: 0 }"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import { jsPDF } from 'jspdf';
import { convertToCDNUrl, getJenisKelamin, getPenerimaDana } from '../../util/PluralVariabels';

const CARD_WIDTH = 650;
const CARD_HEIGHT = 1017;
const CARD_FONT_FAMILY = "'Barlow Condensed', sans-serif";
const QR_FRAME = { x: 31, y: 171, width: 298, height: 313, radius: 38 };
const QR_IMAGE = { x: 42, y: 190, size: 276 };
const PHOTO_FRAME = { x: 299, y: 589, size: 292, radius: 11 };

export default defineComponent({
  name: 'KartuDimas',
  components: { VueQrcode },
  props: {
    santri: {
      type: Object,
      required: true
    },
    qrCodeValue: {
      type: String,
      default: ''
    }
  },
  computed: {
    cardQrValue(): string {
      return this.qrCodeValue || this.santri.qr_code || '';
    },
    photoUrl(): string {
      return this.santri.image ? convertToCDNUrl(this.santri.image) : '';
    },
    genderLabel(): string {
      return getJenisKelamin(this.santri.jenis_kelamin);
    },
    dormLabel(): string {
      return getPenerimaDana(this.santri.wilayah) || this.santri.komplek || this.santri.kamar || '';
    }
  },
  watch: {
    santri: {
      deep: true,
      handler() {
        this.queueDraw();
      }
    },
    qrCodeValue() {
      this.queueDraw();
    }
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    queueDraw() {
      this.$nextTick(() => this.refresh());
    },

    async refresh() {
      await this.$nextTick();
      if (document.fonts) {
        await Promise.all(
          [600, 700, 800].map((weight) => document.fonts.load(`${weight} 24px "Barlow Condensed"`))
        );
        await document.fonts.ready;
      }

      const [frontTemplate, backTemplate, photo] = await Promise.all([
        this.loadImage('/static/images/background/template_photo.jpeg'),
        this.loadImage('/static/images/background/template_nama.jpeg'),
        this.photoUrl ? this.loadImage(this.photoUrl, true).catch(() => null) : Promise.resolve(null)
      ]);

      this.drawFront(frontTemplate, photo);
      this.drawBack(backTemplate);
    },

    loadImage(source: string, useCors = false): Promise<HTMLImageElement> {
      return new Promise((resolve, reject) => {
        const image = new Image();
        if (useCors) image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = source;
      });
    },

    getContext(refName: string): CanvasRenderingContext2D | null {
      const canvas = this.$refs[refName] as HTMLCanvasElement;
      return canvas ? canvas.getContext('2d') : null;
    },

    drawFront(template: HTMLImageElement, photo: HTMLImageElement | null) {
      const context = this.getContext('frontCanvas');
      if (!context) return;

      context.clearRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
      context.drawImage(template, 0, 0, CARD_WIDTH, CARD_HEIGHT);

      const qrCanvas = this.getQrCanvas();
      if (qrCanvas) {
        context.save();
        this.roundedRect(
          context,
          QR_FRAME.x,
          QR_FRAME.y,
          QR_FRAME.width,
          QR_FRAME.height,
          QR_FRAME.radius
        );
        context.clip();
        context.imageSmoothingEnabled = false;
        context.drawImage(qrCanvas, QR_IMAGE.x, QR_IMAGE.y, QR_IMAGE.size, QR_IMAGE.size);
        context.imageSmoothingEnabled = true;
        context.restore();
      }

      context.save();
      this.roundedRect(
        context,
        PHOTO_FRAME.x,
        PHOTO_FRAME.y,
        PHOTO_FRAME.size,
        PHOTO_FRAME.size,
        PHOTO_FRAME.radius
      );
      context.clip();
      if (photo) {
        this.drawImageCover(
          context,
          photo,
          PHOTO_FRAME.x,
          PHOTO_FRAME.y,
          PHOTO_FRAME.size,
          PHOTO_FRAME.size,
          0.18
        );
      } else {
        context.fillStyle = '#e8f0f2';
        context.fillRect(PHOTO_FRAME.x, PHOTO_FRAME.y, PHOTO_FRAME.size, PHOTO_FRAME.size);
        context.fillStyle = '#7ca3ab';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.font = `700 70px ${CARD_FONT_FAMILY}`;
        context.fillText(
          this.initials(this.santri.nama_lengkap),
          PHOTO_FRAME.x + (PHOTO_FRAME.size / 2),
          PHOTO_FRAME.y + (PHOTO_FRAME.size / 2)
        );
      }
      context.restore();

      const name = this.formatCardText(this.santri.nama_lengkap).toLocaleUpperCase('id-ID');
      this.drawRightLines(context, name, 278, 708, 225, 2, 25, 18, 29, '#182650', 800);
      this.drawFittedText(
        context,
        this.santri.nomor || this.santri.nomor_induk || '-',
        278,
        746,
        225,
        22,
        18,
        '#f05a16',
        800,
        'right'
      );
    },

    drawBack(template: HTMLImageElement) {
      const context = this.getContext('backCanvas');
      if (!context) return;

      context.clearRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
      context.drawImage(template, 0, 0, CARD_WIDTH, CARD_HEIGHT);

      this.drawFittedText(
        context,
        this.santri.nomor || this.santri.nomor_induk || '-',
        466,
        422,
        160,
        25,
        18,
        '#e95b13',
        800
      );

      const fields = [
        { value: this.formatCardText(this.santri.nama_lengkap), y: 462, size: 21 },
        { value: this.santri.nik || '-', y: 500, size: 20 },
        { value: this.formatCardText(this.santri.tempat_tgl_lahir), y: 536, size: 20 },
        { value: this.formatCardText(this.genderLabel), y: 574, size: 20 },
        { value: this.formatCardText(this.santri.orang_tua), y: 612, size: 20 }
      ];

      fields.forEach((field) => {
        this.drawFittedText(context, field.value, 350, field.y, 272, field.size, 15, '#182650', 650);
      });

      this.drawLeftLines(
        context,
        this.formatCardText(this.santri.alamat_lengkap),
        350,
        650,
        272,
        3,
        19,
        15,
        23,
        '#182650',
        650
      );
      this.drawFittedText(context, this.formatCardText(this.dormLabel), 350, 728, 272, 20, 15, '#182650', 650);
      this.drawFittedText(context, this.formatCardText(this.santri.sekolah), 350, 766, 272, 20, 15, '#182650', 650);
    },

    getQrCanvas(): HTMLCanvasElement | null {
      const source = this.$refs.qrSource as any;
      if (!source) return null;
      return (source.$el || source) as HTMLCanvasElement;
    },

    drawFittedText(
      context: CanvasRenderingContext2D,
      value: unknown,
      x: number,
      y: number,
      maxWidth: number,
      preferredSize: number,
      minimumSize: number,
      color: string,
      weight: number,
      align: CanvasTextAlign = 'left'
    ) {
      const text = String(value || '-');
      let size = preferredSize;
      context.textAlign = align;
      context.textBaseline = 'alphabetic';
      context.fillStyle = color;
      do {
        context.font = `${weight} ${size}px ${CARD_FONT_FAMILY}`;
        if (context.measureText(text).width <= maxWidth || size <= minimumSize) break;
        size -= 1;
      } while (size >= minimumSize);
      context.fillText(text, x, y);
    },

    drawRightLines(
      context: CanvasRenderingContext2D,
      value: string,
      rightX: number,
      bottomY: number,
      maxWidth: number,
      maxLines: number,
      preferredSize: number,
      minimumSize: number,
      lineHeight: number,
      color: string,
      weight: number
    ) {
      let size = preferredSize;
      let lines: string[] = [];
      do {
        context.font = `${weight} ${size}px ${CARD_FONT_FAMILY}`;
        lines = this.wrapLines(context, value, maxWidth);
        const widestLine = Math.max(...lines.map((line) => context.measureText(line).width));
        if ((lines.length <= maxLines && widestLine <= maxWidth) || size <= minimumSize) break;
        size -= 1;
      } while (size >= minimumSize);

      lines = lines.slice(0, maxLines);
      const firstY = bottomY - ((lines.length - 1) * lineHeight);
      context.fillStyle = color;
      context.textAlign = 'right';
      context.textBaseline = 'alphabetic';
      context.font = `${weight} ${size}px ${CARD_FONT_FAMILY}`;
      lines.forEach((line, index) => context.fillText(line, rightX, firstY + (index * lineHeight)));
    },

    drawLeftLines(
      context: CanvasRenderingContext2D,
      value: string,
      x: number,
      startY: number,
      maxWidth: number,
      maxLines: number,
      preferredSize: number,
      minimumSize: number,
      lineHeight: number,
      color: string,
      weight: number
    ) {
      let size = preferredSize;
      let lines: string[] = [];
      do {
        context.font = `${weight} ${size}px ${CARD_FONT_FAMILY}`;
        lines = this.wrapLines(context, value, maxWidth);
        if (lines.length <= maxLines || size <= minimumSize) break;
        size -= 1;
      } while (size >= minimumSize);

      context.fillStyle = color;
      context.textAlign = 'left';
      context.textBaseline = 'alphabetic';
      context.font = `${weight} ${size}px ${CARD_FONT_FAMILY}`;
      lines.slice(0, maxLines).forEach((line, index) => context.fillText(line, x, startY + (index * lineHeight)));
    },

    wrapLines(context: CanvasRenderingContext2D, value: string, maxWidth: number): string[] {
      const words = String(value || '-').split(/\s+/).filter(Boolean);
      const lines: string[] = [];
      let current = '';
      words.forEach((word) => {
        const candidate = current ? `${current} ${word}` : word;
        if (current && context.measureText(candidate).width > maxWidth) {
          lines.push(current);
          current = word;
        } else {
          current = candidate;
        }
      });
      if (current) lines.push(current);
      return lines.length ? lines : ['-'];
    },

    drawImageCover(
      context: CanvasRenderingContext2D,
      image: HTMLImageElement,
      x: number,
      y: number,
      width: number,
      height: number,
      verticalFocus: number
    ) {
      const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
      const sourceWidth = width / scale;
      const sourceHeight = height / scale;
      const sourceX = Math.max(0, (image.naturalWidth - sourceWidth) / 2);
      const availableY = Math.max(0, image.naturalHeight - sourceHeight);
      const sourceY = availableY * verticalFocus;
      context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
    },

    roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
      context.beginPath();
      context.moveTo(x + radius, y);
      context.arcTo(x + width, y, x + width, y + height, radius);
      context.arcTo(x + width, y + height, x, y + height, radius);
      context.arcTo(x, y + height, x, y, radius);
      context.arcTo(x, y, x + width, y, radius);
      context.closePath();
    },

    initials(value: unknown): string {
      return String(value || 'S')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toLocaleUpperCase('id-ID'))
        .join('') || 'S';
    },

    formatCardText(value: unknown): string {
      if (value === null || value === undefined || String(value).trim() === '') return '-';
      return String(value)
        .toLocaleLowerCase('id-ID')
        .replace(/(^|[\s,/.-])([a-zà-ÿ])/g, (match, separator, letter) => `${separator}${letter.toLocaleUpperCase('id-ID')}`);
    },

    async downloadPrintPdf(filename: string) {
      await this.refresh();
      const front = this.$refs.frontCanvas as HTMLCanvasElement;
      const back = this.$refs.backCanvas as HTMLCanvasElement;
      if (!front || !back) throw new Error('Canvas kartu belum siap');

      const cardWidthMm = 54;
      const cardHeightMm = 85.6;
      const printMarginMm = 3;
      const pageWidthMm = cardWidthMm + (printMarginMm * 2);
      const pageHeightMm = cardHeightMm + (printMarginMm * 2);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidthMm, pageHeightMm],
        compress: true
      });

      pdf.addImage(
        front.toDataURL('image/png'),
        'PNG',
        printMarginMm,
        printMarginMm,
        cardWidthMm,
        cardHeightMm,
        undefined,
        'FAST'
      );
      pdf.addPage([pageWidthMm, pageHeightMm], 'portrait');
      pdf.addImage(
        back.toDataURL('image/png'),
        'PNG',
        printMarginMm,
        printMarginMm,
        cardWidthMm,
        cardHeightMm,
        undefined,
        'FAST'
      );
      pdf.save(filename);
    }
  }
});
</script>

<style lang="scss" scoped>
.kartu-export-engine {
  position: fixed;
  left: -10000px;
  top: 0;
  width: 650px;
  height: 1017px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.qr-render-source {
  width: 276px;
  height: 276px;
  overflow: hidden;
}
</style>
